/** Тема: выбор пользователя в localStorage, иначе системная. */

import { Monitor, Moon, Sun } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { cn } from '@/components/ui/primitives'

type ThemeMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'askona-web:theme'

interface ThemeContextValue {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
}

function applyMode(mode: ThemeMode) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const dark = mode === 'dark' || (mode === 'system' && prefersDark)
  document.documentElement.classList.toggle('dark', dark)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode())

  useEffect(() => {
    applyMode(mode)
    if (mode !== 'system') return
    // В системном режиме следим за сменой темы ОС на ходу.
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyMode('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [mode])

  const setMode = useCallback((next: ThemeMode) => {
    localStorage.setItem(STORAGE_KEY, next)
    setModeState(next)
  }, [])

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme вызван вне ThemeProvider')
  return context
}

const OPTIONS: Array<{ mode: ThemeMode; label: string; Icon: typeof Sun }> = [
  { mode: 'light', label: 'Светлая тема', Icon: Sun },
  { mode: 'dark', label: 'Тёмная тема', Icon: Moon },
  { mode: 'system', label: 'Как в системе', Icon: Monitor },
]

export function ThemeToggle() {
  const { mode, setMode } = useTheme()
  return (
    <div
      className="flex items-center gap-0.5 rounded-full border border-line bg-surface p-0.5"
      role="group"
      aria-label="Тема оформления"
    >
      {OPTIONS.map(({ mode: option, label, Icon }) => (
        <button
          key={option}
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={mode === option}
          onClick={() => setMode(option)}
          className={cn(
            'cursor-pointer rounded-full p-1.5 transition-colors',
            mode === option
              ? 'bg-brand-800 text-white dark:bg-brand-600'
              : 'text-ink-subtle hover:bg-surface-muted hover:text-ink',
          )}
        >
          <Icon className="size-4" />
        </button>
      ))}
    </div>
  )
}
